<script setup lang="ts">
import { computed, inject, useTemplateRef, ref, watch } from "vue";
import { storeToRefs } from "pinia";

import PanelMenu, { PanelMenuPanelOpenEvent } from 'primevue/panelmenu';
import {PanelMenuExpandedKeys} from 'primevue/panelmenu';

import SidebarLabelMenuItem from "@/components/navigation/SidebarLabelMenuItem.vue";
import SidebarRouterMenuItem from "@/components/navigation/SidebarRouterMenuItem.vue";
import SidebarActionMenuItem from "@/components/navigation/SidebarActionMenuItem.vue";
import SignoutModal from "@/components/modals/SignoutModal.vue";

import { useProductsStore } from "@/stores/Products";
import { useUserStore } from "@/stores/User";

import { useMenuPermissions } from "@/composables/menu-permissions";
import { useVersionInfo } from "@/services/version-service";

import type { LabelService } from "@/composables/label-service";
import type { NavItem } from "@tmi/types/navigation";
import type { Product } from "@tmi/types/entities";

import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";
import routeManager from "@/router/route-manager";
import { useRoute } from 'vue-router';

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;
const { currentVersion } = useVersionInfo();
const productsStore = useProductsStore();
const { availableProducts, subscribedProducts } = storeToRefs( productsStore );
const userStore = useUserStore();
const {
  // isAllianceMember,
  showBonusReportOption,
  showCoursesMenuOption,
  showEtfListingMenuOption,
  showLearningMenuItem,
  showMajorIndexBreadthMenuOption,
  showNyseBpiMenuOption,
  showOptionsCalculatorMenuOption,
  showPortfolioBuilderMenuOption,
  showPositionSizeCalculatorMenuOption,
  showRelativeStrengthMatrixMenuOption,
  showSectorAlertsMenuOption,
  showSectorBpiMenuOption,
  showSectorsMenuOption,
  showSectorTreeMenuOption,
  showSpreadCalculatorMenuOption,
  showToolsMenuItem,
  showTradeAlertsMenuOption,
  // showTradeOfTheDayMenuOption,
  showWebinarsMenuOption,
  showAlertsMenuItem,
  showArticlesOption
} = useMenuPermissions();

const productsNavItems = computed< NavItem[] >( () => {
  let products: NavItem[] = [];

/*
  if ( !isAllianceMember.value ) {
    products.push( {
      id: 'product-Alliance-menuitem',
      key: 'product-Alliance-menuitem',
      label: labelService.label( 'lblAlliance' ),
      route: '/',
      icon: 'pi-lock',
      visible: true
    } );
  }
*/

    products.push( {
      id: "sector-edge-menuitem",
      key: "sector-edge-menuitem",
      label: labelService.label( 'lblSectorEdge' ),
      route: routeManager.sectorEdgePath(),
      visible: true,
      image: "/img/logos/sectoredge-logo-32-32.png"
    } );

  subscribedProducts.value.forEach( ( product: Product ) => {
    products.push( {
      id: `product-${ product.slug }-menuitem`,
      key: `product-${ product.slug }-menuitem`,
      label: product.name,
      route: `/products/${ product.slug }`,
      image: product.favicon,
      visible: true
    } );
  } );

  availableProducts.value.forEach( ( product: Product ) => {
    products.push( {
      id: `product-${ product.slug }-menuitem`,
      key: `product-${ product.slug }-menuitem`,
      label: product.name,
      route: `/products/${ product.slug }`,
      icon: 'pi-lock',
      visible: true
    } );
  } );

  return products;
} );

const recurlyHostedLink = computed< string | undefined >( () => userStore.recurlyHostedLink );

const signoutModalRef = useTemplateRef( 'signoutModal' );

function showSignoutModal() {
  signoutModalRef.value?.open();
}

const subscriptionsMenuKey = "subscriptions-menu";
const dashboardMenuKey = "dashboard-menu";

const menuItems = computed< NavItem[] >( () => {
      return [
       {
        label: labelService.label( 'lblDashboard' ),
        id: dashboardMenuKey,
        key: dashboardMenuKey,
        visible: true,
        route: routeManager.homePath(),
        icon: "pi-gauge",
        class: "text-base",
        isRootItem: true
      },
      {
        label: labelService.label( 'lblServices' ),
        id: subscriptionsMenuKey,
        key: subscriptionsMenuKey,
        visible: true,
        items: productsNavItems.value,
        icon: 'pi-th-large',
        isRootItem: true
      },
      {
        label: labelService.label( 'lblMarketAlerts' ),
        id: "alerts-menu",
        key: "alerts-menu",
        visible: showAlertsMenuItem.value,
        icon: 'pi-exclamation-triangle',
        isRootItem: true,
        items: [ {
          id: "sector-alerts-menuitem",
          key: "sector-alerts-menuitem",
          label: labelService.label( 'lblSectorAlerts' ),
          route: routeManager.sectorAlertsPath(),
          visible: showSectorAlertsMenuOption.value,
          icon: "pi-bolt"
        }, {
          id: "trade-alerts-menuitem",
          key: "trade-alerts-menuitem",
          label: labelService.label( 'lblTradeAlerts' ),
          route: routeManager.tradeAlertsPath(),
          visible: showTradeAlertsMenuOption.value,
          icon: "pi-chart-line"
        },
        //   {
        //   id: "trade-of-the-day",
        //   key: "trade-of-the-day-menuitem",
        //   label: labelService.label( 'lblTradeOfTheDay' ),
        //   route: routeManager.tradeOfTheDayPath(),
        //   visible: showTradeOfTheDayMenuOption.value,
        //   icon: "pi-discord"
        // }
        ]
      },
      {
        label: labelService.label( 'lblLearning' ),
        id: "learning-menu",
        key: "learning-menu",
        visible: showLearningMenuItem.value,
        icon: 'pi-lightbulb',
        isRootItem: true,
        items: [ {
          id: "articles-menuitem",
          key: "articles-menuitem",
          label: labelService.label( 'lblArticles' ),
          route: routeManager.articleSearchPath(),
          visible: showArticlesOption.value,
          icon: "pi-folder"
        }, {
          id: "bonus-reports-menuitem",
          key: "bonus-reports-menuitem",
          label: labelService.label( 'lblBonusReports' ),
          route: routeManager.bonusReportsSearchPath(),
          visible: showBonusReportOption.value,
          icon: "pi-file-pdf"
        }, {
          id: "cources-menuitem",
          key: "courses-menuitem",
          label: labelService.label( 'lblCourses' ),
          route: routeManager.coursesPath(),
          visible: showCoursesMenuOption.value,
          icon: "pi-graduation-cap"
        },
        {
          id: "webinars-menuitem",
          key: "webinars-menuitem",
          label: labelService.label( 'lblWebinars' ),
          route: routeManager.webinarSearchPath(),
          visible: showWebinarsMenuOption.value,
          icon: "pi-video"
        }
      ]
      }, {
        label: labelService.label( 'lblTools' ),
        id: "tools-menu",
        key: "tools-menu",
        visible: showToolsMenuItem.value,
        icon: 'pi-hammer',
        isRootItem: true,
        items: [
        {
          id: "Sectors-menuitem",
          key: "Sectors-menuitem",
          label: labelService.label( 'lblSectors' ),
          route: routeManager.sectorListingPath(),
          visible: showSectorsMenuOption.value,
          icon: "pi-list"
        },
        {
          id: "SectorTree-menuitem",
          key: "SectorTree-menuitem",
          label: labelService.label( 'lblSectorTree' ),
          route: routeManager.sectorTreePath(),
          visible: showSectorTreeMenuOption.value,
          icon: "pi-list"
        },
        {
          id: "EtfListing-menuitem",
          key: "EtfListing-menuitem",
          label: labelService.label( 'lblEtfListing' ),
          route: routeManager.etfListingPath(),
          visible: showEtfListingMenuOption.value,
          icon: "pi-list"
        },
        {
          id: "RsMatrix-menuitem",
          key: "RsMatrix-menuitem",
          label: labelService.label( 'lblRsMatrix' ),
          route: routeManager.relativeStrengthMatrixPath(),
          visible: showRelativeStrengthMatrixMenuOption.value,
          icon: "pi-chart-bar"
        },
        {
          id: "sectorbpis-menuitem",
          key: "sectorbpis-menuitem",
          label: labelService.label( 'lblSectorBpis' ),
          route: routeManager.sectorBullishPercentIndicesPath(),
          visible: showSectorBpiMenuOption.value,
          icon: "pi-chart-line"
        },
        {
          id: "MajorIndexBreadth-menuitem",
          key: "MajorIndexBreadth-menuitem",
          label: labelService.label( 'lblMajorIndexBreadth' ),
          route: routeManager.majorIndexBreadthPath(),
          visible: showMajorIndexBreadthMenuOption.value,
          icon: "pi-chart-line"
        },
        {
          id: "NyseBpi-menuitem",
          key: "NyseBpi-menuitem",
          label: labelService.label( 'lblNyseBpi' ),
          route: routeManager.nyseBullishPercentIndexPath(),
          visible: showNyseBpiMenuOption.value,
          icon: "pi-chart-line"
        },
        {
          id: "OptionsCalculator-menuitem",
          key: "OptionsCalculator-menuitem",
          label: labelService.label( 'lblOptionsCalculator' ),
          route: routeManager.optionsCalculatorPath(),
          visible: showOptionsCalculatorMenuOption.value,
          icon: "pi-calculator"
        },
        {
          id: "PortfolioBuilder'-menuitem",
          key: "PortfolioBuilder'-menuitem",
          label: labelService.label( 'lblPortfolioBuilder' ),
          route: routeManager.portfolioBuilderPath(),
          visible: showPortfolioBuilderMenuOption.value,
          icon: "pi-briefcase"
        },
        {
          id: "PositionSizeCalculator-menuitem",
          key: "PositionSizeCalculator-menuitem",
          label: labelService.label( 'lblPositionSizeCalculator' ),
          route: routeManager.positionSizeCalculatorPath(),
          visible: showPositionSizeCalculatorMenuOption.value,
          icon: "pi-calculator"
        },
        {
          id: "SpreadCalculators-menuitem",
          key: "SpreadCalculators-menuitem",
          label: labelService.label( 'lblSpreadCalculators' ),
          route: routeManager.spreadCalculatorPath(),
          visible: showSpreadCalculatorMenuOption.value,
          icon: "pi-calculator"
        }
      ]
      }, {
        label: labelService.label( 'lblMyAccount' ),
        id: "account-menu",
        key: "account-menu",
        visible: true,
        icon: 'pi-user',
        isRootItem: true,
        items: [ {
            id: "Profile-menu",
            key: "Profile-menu",
            label: labelService.label( 'lblProfile' ),
            visible: true,
            route: routeManager.profileBasePath(),
            icon: "pi-user"
          },
          {
            id: "MyLibrary-menu",
            key: "MyLibrary-menu",
            label: labelService.label( 'lblMyLibrary' ),
            visible: true,
            route: routeManager.myLibraryPath(),
            icon: "pi-book"
          },
          {
            id: "Billing-menu",
            key: "Billing-menu",
            label: labelService.label( 'lblBilling' ),
            visible: !!recurlyHostedLink.value,
            icon: "pi-credit-card",
            action: () => window.open( recurlyHostedLink.value, '_blank' )
          },
          {
            id: "SignOut-menu",
            key: "SignOut-menu",
            label: labelService.label( 'lblSignOut' ),
            visible: true,
            icon: "pi-sign-out",
            action: () => showSignoutModal()
          }
        ]
      }
    ];
  }
);

const expandedMenuItemKeys = ref(< PanelMenuExpandedKeys >{ });
const onPanelMenuPanelOpened = (event: PanelMenuPanelOpenEvent) => {
  //The point of this function is to enforce a single-panel-expanded behavior without preventing us from being able to set a default.
  //The PanelMenu component does not allow a default to be expanded without also having the multiple selection behavior enabled.

  if(event.item.key === dashboardMenuKey)
    expandedMenuItemKeys.value = {[subscriptionsMenuKey]: true };
  else if(event.item.key !== subscriptionsMenuKey)
    expandedMenuItemKeys.value = {[event.item.key]: true };
};

const menuPtStyle = {
  header:
    "cursor-pointer select-none touch-manipulation transition-colors duration-150 hover:bg-base-200/60 dark:hover:bg-base-200/30",
  headerContent: "cursor-pointer select-none min-w-0",
  submenulabel: "border-t border-neutral-600 mx-2 mt-3 pt-5 pb-2 tracking-wider",
  itemcontent: "m-0 pl-2 cursor-pointer select-none touch-manipulation",
  item: { id: "menu-item", class: "cursor-pointer select-none" },
};

function openInitialMenuPanel() {
  const route = useRoute();
  const path = route.path
  const parentMenuItem = menuItems.value.find((parent) => parent.items?.find((child) => child.route === path));

  if(parentMenuItem) {
    expandedMenuItemKeys.value = {[parentMenuItem.key]: true };
  }
  else {
    expandedMenuItemKeys.value = {[subscriptionsMenuKey]: true };
  }
}

watch(
  () => menuItems.value.length,
  () => {
    //thw watch is needed because the menu items array is computed and is not available when the page loads and when the menu component is initialized.
    openInitialMenuPanel();
  },
  { immediate: true }
);

</script>

<template>
  <div class="flex flex-col h-full">

    <router-link :to="routeManager.homePath()" class="shrink-0 tmi-logo w-32 min-w-32 max-w-32 p-2 h-9 lg:hidden block" />

    <PanelMenu :model="menuItems" multiple:false unstyled :pt="menuPtStyle" v-on:panel-open="onPanelMenuPanelOpened" v-model:expandedKeys="expandedMenuItemKeys">
      <template #item="{ item }">
        <SidebarRouterMenuItem v-if="item.visible && item.route" :item="item"></SidebarRouterMenuItem>
        <SidebarActionMenuItem v-else-if="item.visible && item.action" :item="item"></SidebarActionMenuItem>
        <SidebarLabelMenuItem v-else="item.visible" :item="item"></SidebarLabelMenuItem>
      </template>
    </PanelMenu>

    <div class="mt-auto p-3 text-xs text-base-content/50 text-center">
      <p class="text-xs mb-0">v{{ currentVersion }} © True Market Insiders 2016-{{ new Date().getFullYear() }}.</p>
      <p class="text-xs mb-0">All rights reserved.</p>
    </div>

  </div>
  <SignoutModal ref="signoutModal"></SignoutModal>
</template>